import React, { FC, memo, useMemo, useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { useScroll, useThrottle } from 'ahooks';
import type { Components } from 'react-markdown';
import type { ISkillData } from '../../../types';
import type { ISkillDisplayViewModel } from '../../../types/skillDetail';
import {
  PERSONAL_TOKEN_CENTER_URL,
  SKILL_DETAIL_SECTION_ICONS,
} from '../../../constants/skillDetail';
import { getSkillMdContent } from '../../../utils/skillMd';
import { getRecommendSkills } from '../../../utils/recommendSkills';
import { extractHeadings, type IHeadingItem } from '../../../utils/mdHeadings';
import EllipsisHover from '@gp/components/EllipsisHover';
import SkillMarkdownViewer from '../SkillMarkdownViewer';
import SkillRecommendList from '../SkillRecommendList';
import './index.scss';

/**
 * 使用说明 Tab 内容 Props
 */
export interface ISkillUsageSectionProps {
  /** 展示视图模型（displayName / description 等） */
  display: ISkillDisplayViewModel;
  /** 当前 Skill 原始数据（正文直接读取 zh_md / en_md 字段） */
  skill: ISkillData;
  /** 全量 Skill 列表（用于推荐列表计算） */
  allSkills: ISkillData[];
  /** 是否英文环境 */
  isEN: boolean;
  /** 推荐 Skill 点击回调 */
  onRecommendSkillClick: (skillId: string) => void;
}

const PREFIX = 'dba-skill-usage-section';

/**
 * 使用说明 Tab 内容
 */
const SkillUsageSection: FC<ISkillUsageSectionProps> = ({
  display,
  skill,
  allSkills,
  isEN,
  onRecommendSkillClick,
}) => {
  const { formatMessage } = useIntl();
  const skillId = skill.skill_id;
  const md = useMemo(() => getSkillMdContent(skill, isEN), [skill, isEN]);

  const hasRecommendSkills = useMemo(
    () => getRecommendSkills(allSkills, skillId).length > 0,
    [allSkills, skillId],
  );

  // 提取 md 标题用于 TOC
  const TOC_MAX_LEVEL = 6;
  const headings = useMemo<IHeadingItem[]>(
    () => (md?.content ? extractHeadings(md.content, TOC_MAX_LEVEL) : []),
    [md?.content],
  );
  const hasToc = headings.length > 0;

  // 自定义 heading 组件：注入锚点 id
  const headingComponents = useMemo<Components>(() => {
    const headingTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
    const components: Components = {};
    headingTags.forEach((Tag) => {
      const HeadingComponent = ({ children, ...rest }: any) => {
        const textContent = extractTextFromChildren(children);
        const slug = toSlug(textContent);
        return <Tag id={slug} style={{ scrollMarginTop: 16 }} {...rest}>{children}</Tag>;
      };
      HeadingComponent.displayName = `MarkdownHeading(${Tag})`;
      components[Tag] = HeadingComponent;
    });
    return components;
  }, []);

  // 点击目录项 → 平滑滚动到锚点
  const handleTocClick = useCallback((id: string) => {
    const container = document.querySelector('.dba-skill-detail-tabs__panel');
    const el = document.getElementById(id);
    if (!(container instanceof HTMLElement) || !el) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const offset = elRect.top - containerRect.top + container.scrollTop - 16;
    container.scrollTo({ top: offset, behavior: 'smooth' });
  }, []);

  return (
    <div className={PREFIX}>
      <div
        className={classNames(`${PREFIX}__top`, {
          [`${PREFIX}__top--no-aside`]: !hasRecommendSkills,
        })}
      >
        <div className={`${PREFIX}__main`}>
          <section className={`${PREFIX}__block`}>
            <h3 className={`${PREFIX}__title`}>
              <img
                src={SKILL_DETAIL_SECTION_ICONS.overview}
                alt=""
                className={`${PREFIX}__title-icon`}
              />
              <span>
                {formatMessage({ id: 'dba_skill_detail_overview_title' })}
              </span>
            </h3>
            <p className={`${PREFIX}__description`}>{display.description}</p>
          </section>

          <section className={`${PREFIX}__block`}>
            <h3 className={`${PREFIX}__title`}>
              <img
                src={SKILL_DETAIL_SECTION_ICONS.token}
                alt=""
                className={`${PREFIX}__title-icon`}
              />
              <span>
                {formatMessage({ id: 'dba_skill_detail_token_title' })}
              </span>
            </h3>
            <p className={`${PREFIX}__token-tip`}>
              {formatMessage({ id: 'dba_skill_detail_token_tip_prefix' })}
              <a
                className={`${PREFIX}__token-link`}
                href={PERSONAL_TOKEN_CENTER_URL}
                target="_blank"
                rel="noreferrer"
              >
                {formatMessage({ id: 'dba_skill_detail_token_link' })}
              </a>
              {formatMessage({ id: 'dba_skill_detail_token_tip_suffix' })}
            </p>
          </section>
        </div>

        {hasRecommendSkills && (
          <div className={`${PREFIX}__aside`}>
            <SkillRecommendList
              allSkills={allSkills}
              currentSkillId={skillId}
              isEN={isEN}
              onSkillClick={onRecommendSkillClick}
            />
          </div>
        )}
      </div>

      <SkillMarkdownViewer
        content={md?.content}
        loading={false}
        error={null}
        components={headingComponents}
        aside={hasToc ? (
          <SkillToc
            headings={headings}
            handleTocClick={handleTocClick}
            prefix={PREFIX}
          />
        ) : undefined}
      />
    </div>
  );
};

// ==================== TOC 子组件 ====================

interface ISkillTocProps {
  headings: IHeadingItem[];
  handleTocClick: (id: string) => void;
  prefix: string;
}

const SkillToc: FC<ISkillTocProps> = ({ headings, handleTocClick, prefix }) => {
  const scroll = useThrottle(useScroll(
    () => document.querySelector('.dba-skill-detail-tabs__panel'),
  ), { wait: 100 });

  const [activeHeadingId, setActiveHeadingId] = useState<string | undefined>(
    () => headings[0]?.id,
  );

  useEffect(() => {
    const panel = document.querySelector('.dba-skill-detail-tabs__panel');
    const panelTop = panel?.getBoundingClientRect().top ?? 0;
    const containerTop = panelTop + 16;
    let activeId = headings[0]?.id;

    for (const heading of headings) {
      const el = document.getElementById(heading.id);
      if (!el) continue;
      if (el.getBoundingClientRect().top <= containerTop) {
        activeId = heading.id;
      } else {
        break;
      }
    }

    setActiveHeadingId(activeId);
  }, [scroll, headings]);

  const handleClick = useCallback((id: string) => {
    setActiveHeadingId(id);
    handleTocClick(id);
  }, [handleTocClick]);

  return (
    <nav className={`${prefix}__toc`} aria-label="目录">
      <ul className={`${prefix}__toc-list`}>
        {headings.map((item) => (
          <li
            key={item.id}
            className={classNames(`${prefix}__toc-item`, {
              [`${prefix}__toc-item--active`]: item.id === activeHeadingId,
            })}
            style={{ paddingLeft: `${(item.level - 1) * 12}px` }}
          >
            <button
              type="button"
              className={`${prefix}__toc-link`}
              onClick={() => handleClick(item.id)}
            >
              <span className={`${prefix}__toc-number`}>{item.number}</span>
              <span className={`${prefix}__toc-text`}><EllipsisHover text={item.text} /></span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// ==================== 工具函数 ====================

/** 从 React children 中提取纯文本 */
const extractTextFromChildren = (children: any): string => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractTextFromChildren).join('');
  if (children?.props?.children) return extractTextFromChildren(children.props.children);
  return '';
};

/** 文本 → URL 安全锚点 id（与 mdHeadings 中的 toSlug 一致） */
const toSlug = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/[\s]+/g, '-')
    .replace(/[^\w\u4e00-\u9fa5-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export default memo(SkillUsageSection);
